// pages/GuestLists.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiUsers, 
  FiPlus, 
  FiTrash2, 
  FiChevronRight,
  FiEdit2
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import CreateGuestListModal from '../components/CreateGuestListModal';
import supabase from '../services/supabase';
import { useAuth } from '../services/auth';
import '../styles/GuestLists.scss';

const GuestLists = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [guestLists, setGuestLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadGuestLists();
  }, []);

  const loadGuestLists = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('guest_lists')
        .select(`
          *,
          contact_count:guest_list_contacts(count)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Format the count
      const formattedLists = data.map(list => ({
        ...list,
        contact_count: list.contact_count[0]?.count || 0
      }));
      
      setGuestLists(formattedLists);
    } catch (error) {
      console.error('Error loading guest lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGuestList = async (listId, listName) => {
    if (!window.confirm(`Are you sure you want to delete "${listName}"? This will remove all contacts in this list.`)) {
      return;
    }

    try {
      // First delete all contacts
      await supabase
        .from('guest_list_contacts')
        .delete()
        .eq('guest_list_id', listId);

      // Then delete the guest list
      const { error } = await supabase
        .from('guest_lists')
        .delete()
        .eq('id', listId);

      if (error) throw error;

      setGuestLists(guestLists.filter(list => list.id !== listId));
    } catch (error) {
      console.error('Error deleting guest list:', error);
      alert('Failed to delete guest list');
    }
  };

  const handleGuestListCreated = (newList) => {
    setGuestLists([{ ...newList, contact_count: 0 }, ...guestLists]);
    setShowCreateModal(false);
    navigate(`/guest-list/${newList.id}`);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading guest lists...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="guest-lists-page">
        <div className="guest-lists-container">
          {/* Header */}
          <motion.div 
            className="page-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="header-content">
              <h1>Guest Lists</h1>
              <p>Create and manage reusable guest lists for your events</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <FiPlus />
              <span>Create Guest List</span>
            </button>
          </motion.div>

          {/* Guest Lists Grid */}
          {guestLists.length === 0 ? (
            <motion.div 
              className="empty-state glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <FiUsers size={64} />
              <h2>No Guest Lists Yet</h2>
              <p>Create guest lists to reuse across multiple events</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <FiPlus />
                <span>Create Your First Guest List</span>
              </button>
            </motion.div>
          ) : (
            <div className="guest-lists-grid">
              {guestLists.map((list, index) => (
                <motion.div
                  key={list.id}
                  className="guest-list-card glass-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => navigate(`/guest-list/${list.id}`)}
                >
                  <div className="card-header">
                    <div className="icon-wrapper">
                      <FiUsers size={24} />
                    </div>
                    <div className="card-actions">
                      <button
                        className="icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGuestList(list.id, list.name);
                        }}
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="card-content">
                    <h3>{list.name}</h3>
                    {list.description && (
                      <p className="description">{list.description}</p>
                    )}
                    <div className="contact-count">
                      <span className="count">{list.contact_count}</span>
                      <span className="label">
                        {list.contact_count === 1 ? 'contact' : 'contacts'}
                      </span>
                    </div>
                  </div>

                  <div className="card-footer">
                    <button className="view-btn">
                      <span>View Details</span>
                      <FiChevronRight />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <CreateGuestListModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleGuestListCreated}
      />
    </DashboardLayout>
  );
};

export default GuestLists;